import {
  API_V1,
  convertGuestResponseSchema,
  errorEnvelopeSchema,
  playerProfileSchema,
  type ConvertGuestResponse,
  type ConversionFailureReason,
  type PlayerProfile,
} from "@wr/contracts";
import { t } from "@wr/i18n";
import { API_URL, hasApiUrl } from "../env";
import { ApiRequestError } from "./api";
import { setAuthTransition } from "./auth-transition";

/**
 * The SPA's auth client (phase plan S2-1/S2-3; the screens that use it land in
 * S2-5).
 *
 * ── Two things about it that are not obvious ────────────────────────────────
 *
 * **1. Every call is `credentials: "include"`.** `web` and `api` are on
 * different origins in every deployed environment, so the session cookie is
 * cross-site: without this the browser sends nothing and every request looks
 * like a logged-out one. (The cookie is also *third-party* until the api gets a
 * `*.wordyroyale.com` domain — see `infra/README.md` §3.1. That is a DNS fix,
 * not a code one.)
 *
 * **2. The WS access token is fetched, never stored.** It lives 5 minutes
 * (architecture §7), so caching it anywhere durable buys nothing and risks
 * handing an expired token to a join. `wsToken()` asks for a fresh one each
 * time; the session cookie is the only long-lived credential the client holds,
 * and it is HttpOnly — this module cannot read it, which is the point.
 */

const AUTH_BASE = "/api/auth";

export class AuthError extends ApiRequestError {
  /** Better Auth's own error code, e.g. `INVALID_EMAIL_OR_PASSWORD`. */
  readonly authCode: string | undefined;
  /**
   * Seconds until a lockout or a rate limit lifts, when the server sent one.
   *
   * Carried on the error rather than read from a header at the call site: the
   * design's `m:ss` countdown (§6 `error-lockout`, §7 `rate-limited`) is the
   * whole reason the API returns the number, and a screen that has to reach
   * back into a `Response` for it will eventually forget to.
   */
  readonly retryAfterSeconds: number | undefined;

  constructor(message: string, status: number, authCode?: string, retryAfterSeconds?: number) {
    super(message, status, authCode);
    this.name = "AuthError";
    this.authCode = authCode;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** A refused conversion, carrying the reason the sheet branches on (§14). */
export class ConversionError extends ApiRequestError {
  readonly reason: ConversionFailureReason | undefined;

  constructor(message: string, status: number, reason?: ConversionFailureReason) {
    super(message, status, "conflict");
    this.name = "ConversionError";
    this.reason = reason;
  }
}

function base(): string {
  if (!hasApiUrl()) throw new ApiRequestError(t("error.missingApiUrl"), 0, "unavailable");
  return API_URL;
}

async function call(
  path: string,
  init: RequestInit = {},
): Promise<{ status: number; body: unknown }> {
  let response: Response;
  try {
    response = await fetch(`${base()}${path}`, {
      credentials: "include",
      ...init,
      headers: {
        accept: "application/json",
        ...(init.body ? { "content-type": "application/json" } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiRequestError(t("error.network"), 0, "unavailable");
  }

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  return { status: response.status, body };
}

/**
 * Better Auth answers with `{ code, message }`, our own routes with the
 * `{ error: { code, message, details } }` envelope. One reader for both, so
 * screens never have to know which tier refused them.
 */
function fail(status: number, body: unknown): never {
  const envelope = errorEnvelopeSchema.safeParse(body);
  if (envelope.success) {
    const details = envelope.data.error.details as { reason?: ConversionFailureReason } | undefined;
    if (details?.reason) {
      throw new ConversionError(envelope.data.error.message, status, details.reason);
    }
    throw new ApiRequestError(envelope.data.error.message, status, envelope.data.error.code);
  }

  const authError = body as
    { code?: string; message?: string; retryAfterSeconds?: number } | undefined;
  throw new AuthError(
    authError?.message ?? t("error.unexpected"),
    status,
    authError?.code,
    authError?.retryAfterSeconds,
  );
}

async function authCall(path: string, init: RequestInit = {}): Promise<unknown> {
  const { status, body } = await call(`${AUTH_BASE}${path}`, init);
  if (status >= 400) fail(status, body);
  return body;
}

// ---- session ---------------------------------------------------------------

export interface SessionUser {
  id: string;
  email: string;
  emailVerified: boolean;
  isAnonymous: boolean;
}

/** The signed-in auth user, or `null`. Never throws for "logged out". */
export async function fetchSession(): Promise<SessionUser | null> {
  const { status, body } = await call(`${AUTH_BASE}/get-session`);
  if (status === 401 || body === null || body === undefined) return null;
  if (status >= 400) fail(status, body);

  const user = (body as { user?: Record<string, unknown> }).user;
  if (!user) return null;
  return {
    id: String(user.id),
    email: String(user.email ?? ""),
    emailVerified: user.emailVerified === true,
    isAnonymous: user.isAnonymous === true,
  };
}

/** "Continue as Guest" (PRD §4.4). */
export async function signInAnonymous(): Promise<void> {
  await authCall("/sign-in/anonymous", { method: "POST", body: "{}" });
}

export async function signInEmail(email: string, password: string): Promise<void> {
  await authCall("/sign-in/email", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

/**
 * Sign-up. `tosAccepted` is not optional and not a formality: the API's
 * `before` hook refuses the request without it (`TOS_NOT_ACCEPTED`), and the
 * consent is recorded as `players.tos_accepted_at` using the server's clock.
 * The checkbox in the form is what makes this true, not what makes it work.
 */
export async function signUpEmail(
  email: string,
  password: string,
  tosAccepted: boolean,
  next?: string | null,
): Promise<void> {
  await authCall("/sign-up/email", {
    method: "POST",
    body: JSON.stringify({ email, password, tosAccepted, ...mailTarget(next) }),
  });
}

/**
 * Resend the verification mail (design spec §7).
 *
 * The API rate-limits this **per email address**, not per IP, so a shared
 * connection is not one shared allowance. A 429 carries `retryAfterSeconds`,
 * which is what the screen's countdown re-arms from.
 */
export async function resendVerification(email: string, next?: string | null): Promise<void> {
  await authCall("/send-verification-email", {
    method: "POST",
    body: JSON.stringify({ email, ...mailTarget(next) }),
  });
}

/**
 * Where a verification mail's link should land, as the one field the client is
 * allowed to have an opinion about (design spec §14.2 item 5).
 *
 * ── Why this is a path and not a `callbackURL` (T-190) ──────────────────────
 *
 * These calls used to send a whole `${WEB_ORIGIN}/login?verified=1` URL and the
 * API took the `next` back out of it. That worked, but it meant the string in a
 * player's inbox started life in a browser, and the endpoint is reachable
 * without a session (QA T-177 N9). The API now composes the link from its own
 * `WEB_APP_URL` and accepts only this path, so the client's job is to say
 * *where in the app*, never *which URL* — and there is no longer a client-built
 * URL anywhere in the mail path to audit.
 *
 * Omitted entirely when there is no target, so "no deep link" is an absent
 * field rather than an empty string the server has to interpret.
 */
function mailTarget(next: string | null | undefined): { next?: string } {
  return next ? { next } : {};
}

/**
 * "Send reset link" (design spec §8.1).
 *
 * **LOCKED — identical success regardless of whether the email exists.** This
 * function therefore has no failure branch a caller could use to tell the two
 * apart: the server answers the same either way, and the screen renders the
 * same confirmation panel on every non-transport outcome.
 */
export async function requestPasswordReset(email: string, redirectTo: string): Promise<void> {
  await authCall("/request-password-reset", {
    method: "POST",
    body: JSON.stringify({ email, redirectTo }),
  });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await authCall("/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  });
}

export async function signOut(): Promise<void> {
  await authCall("/sign-out", { method: "POST", body: "{}" });
}

/**
 * A fresh 5-minute WS access token (architecture §7).
 *
 * This is the ONLY thing the browser gives the realtime tier: the game verifies
 * it against the API's JWKS and derives the player identity from its claims.
 * The client never sends a player id anywhere (LOCKED, S2-4).
 */
export async function wsToken(): Promise<string> {
  const body = await authCall("/token");
  const token = (body as { token?: unknown }).token;
  if (typeof token !== "string" || token.length === 0) {
    throw new AuthError(t("error.unexpected"), 200);
  }
  return token;
}

// ---- our player identity ---------------------------------------------------

export async function fetchProfile(): Promise<PlayerProfile | null> {
  const { status, body } = await call(`${API_V1}/players/me`);
  if (status === 401) return null;
  if (status >= 400) fail(status, body);
  return playerProfileSchema.parse(body);
}

/**
 * Guest → registered (phase plan S2-3).
 *
 * Note what is NOT sent: which account to convert. The server takes that from
 * the session, so there is no field here for a client to lie in.
 *
 * On success the guest session is already dead — the response cleared the
 * cookie — and the account is unverified. The caller shows the verify-email
 * panel inline without navigating (design spec §14).
 *
 * **§14.1 requirement 2, LOCKED.** The `converting` flag is raised *before* the
 * response is applied, not after: the moment the cookie is cleared, anything
 * else in flight starts 401ing, and a guard that reads one of those 401s
 * without knowing a conversion caused it will redirect the page out from under
 * the sheet. It is lowered again on failure — an `email_in_use` refusal leaves
 * the guest session completely intact, and freezing the app for a form error
 * would be its own bug.
 */
export async function convertGuest(
  email: string,
  password: string,
  tosAccepted: boolean,
  /**
   * Where the player was when they asked to convert (§14.1 r1). Sent so the
   * verification mail's callback can carry it, which is what makes the
   * email-link road out of the success panel land where the CTA road does
   * (QA T-175 N2). The server validates it as a same-origin path; a client
   * that sends something else gets the default landing, never a redirect.
   */
  next?: string,
): Promise<ConvertGuestResponse> {
  setAuthTransition("converting");
  let status: number;
  let body: unknown;
  try {
    ({ status, body } = await call(`${API_V1}/players/me/convert`, {
      method: "POST",
      body: JSON.stringify({ email, password, tosAccepted, ...(next ? { next } : {}) }),
    }));
  } catch (caught) {
    setAuthTransition("none");
    throw caught;
  }

  if (status >= 400) {
    setAuthTransition("none");
    fail(status, body);
  }

  try {
    return convertGuestResponseSchema.parse(body);
  } catch (caught) {
    // A 200 we cannot read still cleared the cookie server-side, but the sheet
    // has no success state to show, so the app must be allowed to resync.
    setAuthTransition("none");
    throw caught;
  }
}
